import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Authenticator } from 'aws-amplify-react';
import { I18n } from 'aws-amplify';
import { Route } from 'react-router-dom';

import AuthEventView from './AuthEventView';
import SignIn from './SignIn';
import SignUp from './SignUp';
import ForgotPassword from './ForgotPassword';
import { useLocation } from 'react-router-dom';
import VerifyUser from './VerifyUser';
import PublicReport from './PublicReport';
import CnReport from './CnReport';

export function isExternalPath(pathname) {
  return [/^(\/signup)/].some(exp => exp.test(pathname));
}

const getState = pathname => {
  const firstPath = pathname.split('/').filter(part => part)[0];
  const newState = pathAuthStateMap[firstPath];
  return newState;
};

function Protected({ authState, children }) {
  // Allow access if CN Auth0 token exists
  const cnToken = localStorage.getItem('cn_ti_token');
  if (cnToken) {
    return children;
  }

  if (
    ['signIn', 'signUp', 'forgotPassword', 'verification'].includes(authState)
  ) {
    return null;
  }

  return children;
}

const pathAuthStateMap = {
  signup: 'signUp',
  login: 'signIn',
  forgot: 'forgotPassword',
  verification: 'verification',
};

const theme = {
  container: {
    width: '100%',
  },
};

const Verification = () => (
  <Route path="/verification" exact>
    <VerifyUser />
  </Route>
);

export function AuthProvider({ children }) {
  const { pathname } = useLocation();

  // Initialize authState - if CN Auth0 token exists, set as 'signedIn' to bypass Cognito
  const cnToken = localStorage.getItem('cn_ti_token');
  const initialState = cnToken ? 'signedIn' : getState(pathname);
  const [authState, setAuthState] = useState(initialState);
  const authenticator = useRef();

  const changeState = useCallback(
    state => {
      authenticator.current.handleStateChange(state);
      authenticator.current.handleAuthEvent(authState, {
        type: 'stateChange',
        data: state,
      });
    },
    [authState, authenticator.current],
  );
  useEffect(() => {
    if (!authenticator.current || !authState) {
      return;
    }
    const newState = getState(pathname);
    if (newState) {
      changeState(newState);
    }
  }, [authenticator.current, pathname, authState]);
  const handleStateChange = useCallback(state => {
    // Don't change auth state if using CN Auth0
    const cnToken = localStorage.getItem('cn_ti_token');
    if (cnToken) {
      console.log(
        'CN Auth0 token present, ignoring Cognito auth state change to:',
        state,
      );
      return;
    }
    setAuthState(state);
  }, []);

  if (pathname.startsWith('/report')) {
    return cnToken ? <CnReport /> : <PublicReport />;
  }

  // For CN Auth0 users, skip Authenticator entirely and render children directly
  if (cnToken) {
    return <Protected>{children}</Protected>;
  }

  return (
    <Authenticator
      ref={authenticator}
      hideDefault
      showToast={false}
      authState={authState}
      onStateChange={handleStateChange}
      theme={theme}
    >
      <SignIn override="signIn" />
      <SignUp override="signUp" />
      <ForgotPassword override="forgotPassword" />
      <Verification override="verification" />
      <AuthEventView />
      <Protected>{children}</Protected>
    </Authenticator>
  );
}

const authScreenLabels = {
  en: {
    'Sign in to your account': ' ',
    Username: 'Email',
    'Sign In': 'Login',
    'Forget your password? ': 'Forgot your password?? ',
    'Enter your username': 'Enter your email',
    'CUSTOM_AUTH is not enabled for the client.': 'You must type a password!',
    'UserMigration failed with error': 'Login failed: ',
  },
};

I18n.setLanguage('en');
I18n.putVocabularies(authScreenLabels);
